import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check } from "lucide-react";
import { landscapeImages } from "@/lib/landscapeCatalog";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

const PAINS = [
  {
    title: "Crews You Can't Hire",
    body: "Mowing and marking are the hardest roles to staff and the first to burn out. Autonomous units cover repetitive acreage so your people handle detail work, client relationships, and higher-margin services.",
  },
  {
    title: "Weather-Compressed Schedules",
    body: "Robots work early mornings, evenings, and overnight with auto-charging or battery swap — recovering days lost to rain without paying overtime.",
  },
  {
    title: "Inconsistent Presentation",
    body: "RTK, LiDAR, and vision guidance repeat the same route and cut height every time, producing consistent stripes, edges, and field lines across every property.",
  },
  {
    title: "Rising Input Costs",
    body: "Robotic line marking reduces paint usage by more than 50%, and electric drivetrains cut energy costs by up to 80% versus conventional equipment.",
  },
];

const SEGMENTS = [
  {
    name: "Golf Courses",
    points: ["Fairway cylinder mowing", "Rough and slope mowing", "Driving-range ball collection", "Dew brushing for consistent ball roll"],
  },
  {
    name: "Sports Complexes & Schools",
    points: ["Autonomous field mowing", "Millimeter-accurate line marking", "Multi-field scheduling", "Game-day turnaround"],
  },
  {
    name: "Municipalities & Parks",
    points: ["Large green-space mowing", "Slope and embankment coverage", "Fleet monitoring and reporting", "Reduced noise near residences"],
  },
  {
    name: "Landscape Contractors",
    points: ["Route-based robot deployment", "Per-property RaaS billing", "Crew reallocation to detail work", "Higher accounts-per-crew ratio"],
  },
];

const LandscapingIndustry = () => (
  <>
    <SEO
      title="Landscaping & Grounds Automation | Neil AI Global Solutions"
      description="Autonomous mowing and line marking for golf courses, sports complexes, municipalities, and landscape contractors. Reduce labor pressure, cut paint and energy costs, and standardize turf presentation."
      keywords="landscaping automation, robotic mowing service, grounds maintenance robots, golf course automation, landscape contractor robotics"
    />
    <Navigation />

    <main className="min-h-screen bg-background pt-32 md:pt-40 pb-20">
      <div className="max-w-7xl mx-auto px-4">
        <section className="relative overflow-hidden rounded-3xl border border-[hsl(var(--line))]">
          <img
            src={landscapeImages.sportsField}
            alt="Autonomous turf robot maintaining a professional sports field"
            className="absolute inset-0 h-full w-full object-cover opacity-35"
          />
          <div className="relative bg-gradient-to-r from-background via-background/85 to-background/40 p-8 md:p-14">
            <Badge className="mb-4 bg-primary/15 text-primary border border-primary/30">
              Industries We Serve
            </Badge>
            <h1 className="font-orbitron text-3xl md:text-5xl font-bold max-w-3xl leading-tight">
              Landscaping &amp; Grounds Maintenance Automation
            </h1>
            <p className="mt-5 max-w-2xl text-muted-foreground md:text-lg">
              Labor shortages are the constraint on growth in green industry operations. Neil AI
              deploys autonomous mowing and line-marking fleets so your crews cover more properties,
              hit tighter windows, and deliver consistent results — without expanding headcount.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  Book a Site Assessment <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full">
                <Link to="/robots/landscape">Browse Landscape Robots</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="mt-14 grid gap-5 md:grid-cols-2">
          {PAINS.map((p) => (
            <Card key={p.title} className="glass-effect border-[hsl(var(--line))] p-6">
              <h2 className="font-orbitron text-lg font-bold">{p.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{p.body}</p>
            </Card>
          ))}
        </section>

        <section className="mt-16">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Who We Deploy For</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SEGMENTS.map((s) => (
              <Card key={s.name} className="glass-effect border-[hsl(var(--line))] p-6">
                <h3 className="font-semibold">{s.name}</h3>
                <ul className="mt-3 space-y-2">
                  {s.points.map((pt) => (
                    <li key={pt} className="flex gap-2 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </section>

        <section className="mt-16">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold">How Deployment Works</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-4">
            {[
              ["1. Site Assessment", "We walk the property, measure acreage, slopes, and obstacles, and quantify current labor hours."],
              ["2. Mapping & Setup", "Boundaries, zones, and routes are mapped with RTK and LiDAR; charging locations are placed."],
              ["3. Crew Training", "Your team learns scheduling, monitoring, blade service, and daily checks — in person and on video."],
              ["4. Ongoing Support", "Fleet monitoring, seasonal tuning, parts, and service through Neil AI's support center."],
            ].map(([title, body]) => (
              <Card key={title} className="glass-effect border-[hsl(var(--line))] p-6">
                <h3 className="font-orbitron text-sm font-bold text-primary">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{body}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="mt-16 rounded-3xl border border-[#FFD700]/25 bg-[#FFD700]/5 p-8 md:p-12 text-center">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold">
            See the Numbers for Your Properties
          </h2>
          <p className="mt-3 mx-auto max-w-2xl text-muted-foreground">
            Bring us your acreage, crew hours, and mowing frequency. We'll model labor savings,
            payback period, and a phased deployment — including RaaS and financing options.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="rounded-full">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book a Consultation
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full">
              <Link to="/roi-calculator">Calculate My ROI</Link>
            </Button>
          </div>
        </section>
      </div>
    </main>

    <Footer />
  </>
);

export default LandscapingIndustry;
